import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import * as Yup from 'yup'

import { useSettingsCheck } from 'api/verification/useSettingsCheck/useSettingsCheck'
import { postModelVerification } from 'services/endpoints/profile'
import { useUserProfile } from 'store'

export interface PersonalInformationFormValues {
  firstName: string
  lastName: string
  country: string | undefined
  address: string
  city: string
  stateProvince: string
  postal: string
  social: { twitter: string; instagram: string; other: string }
  dateOfBirth: string
  documentType: string
  idExpiration: string
  noExpiration: boolean
  idPhotoUrl: string[]
  idHoldPhotoUrl: string[]
  agreeToCreatorTerms: boolean
}

export function usePersonalInformationForm() {
  const { t } = useTranslation()
  const userData = useUserProfile()
  const queryClient = useQueryClient()
  const { data: settingsValue } = useSettingsCheck()

  const PersonalInformationSchema = Yup.object({
    firstName: Yup.string()
      .max(15, t('validation:mustBe15CharactersOrLess'))
      .required(t('validation:required')),

    lastName: Yup.string()
      .max(20, t('validation:mustBe20CharactersOrLess'))
      .required(t('validation:required')),

    country: Yup.string()
      .max(50, t('validation:mustBe50CharactersOrLess'))
      .required(t('validation:required')),

    address: Yup.string()
      .max(100, t('validation:mustBe100CharactersOrLess'))
      .required(t('validation:required')),

    city: Yup.string()
      .max(50, t('validation:mustBe50CharactersOrLess'))
      .required(t('validation:required')),

    stateProvince: Yup.string()
      .max(50, t('validation:mustBe50CharactersOrLess'))
      .required(t('validation:required')),

    postal: Yup.string()
      .max(10, t('validation:mustBe10CharactersOrLess'))
      .required(t('validation:required')),

    social: Yup.object({
      twitter: Yup.string().max(50, t('validation:mustBe50CharactersOrLess')),
      instagram: Yup.string().max(50, t('validation:mustBe50CharactersOrLess')),
      other: Yup.string().max(50, t('validation:mustBe50CharactersOrLess')),
    }).test('at-least-one', t('validation:required'), (value) =>
      Boolean(
        value &&
          (
            value.twitter?.trim() ||
            value.instagram?.trim() ||
            value.other?.trim()
          )
      )
    ),

    dateOfBirth: Yup.date().required(t('validation:required')),

    documentType:
      settingsValue !== 1
        ? Yup.string()
            .max(20, t('validation:mustBe20CharactersOrLess'))
            .required(t('validation:required'))
        : Yup.mixed().notRequired(),

    noExpiration: Yup.boolean(),

    idExpiration:
      settingsValue !== 1
        ? Yup.date()
            .nullable()
            .transform((value, originalValue) => {
              return originalValue === '' ? null : value
            })
            .when('noExpiration', {
              is: true,
              then: schema => schema.notRequired(),
              otherwise: schema => schema.required(t('validation:required')),
            })
        : Yup.mixed().notRequired(),

    idPhotoUrl:
      settingsValue !== 1
        ? Yup.array()
            .of(Yup.string().required(t('validation:required')))
            .min(1, t('validation:required'))
        : Yup.mixed().notRequired(),

    idHoldPhotoUrl:
      settingsValue !== 1
        ? Yup.array()
            .of(Yup.string().required(t('validation:required')))
            .min(1, t('validation:required'))
        : Yup.mixed().notRequired(),

    agreeToCreatorTerms: Yup.boolean().oneOf([true], t('validation:required')),
  })

  const verifyUser = useMutation(
    async (payload: any) => {
      return await postModelVerification(payload)
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('user-profile')
      }
    }
  )

  const formik = useFormik<PersonalInformationFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      country: userData?.country,
      address: '',
      city: '',
      stateProvince: '',
      postal: '',
      social: { twitter: '', instagram: '', other: '' },
      dateOfBirth: '',
      documentType: 'passport',
      idExpiration: '',
      noExpiration: false,
      idPhotoUrl: [],
      idHoldPhotoUrl: [],
      agreeToCreatorTerms: false,
    },
    validationSchema: PersonalInformationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async (values) => {
      const data: any = {
        first_name: values.firstName,
        last_name: values.lastName,
        country_name: values.country,
        address: values.address,
        city_name: values.city,
        state: values.stateProvince,
        postal_code: values.postal,
        twitter: values.social.twitter,
        instagram: values.social.instagram,
        other: values.social.other,
        date_of_birth: values.dateOfBirth
          ? new Date(values.dateOfBirth).toISOString().slice(0, 10)
          : null,
        agree_to_creator_terms: values.agreeToCreatorTerms,
      }

      if (settingsValue !== 1) {
        Object.assign(data, {
          id_expiration_date: values.noExpiration ? null : values.idExpiration,
          verification_document_type: values.documentType,
          no_expired_date: values.noExpiration,
          id_photo: values.idPhotoUrl[0],
          holding_id_photo: values.idHoldPhotoUrl[0],
        })
      }

      return await verifyUser.mutateAsync(data)
    },
  })

  return { formik, settingsValue, verifyUser }
}